export default {
  slug: "/periodontics",
  group: { href: "/dental", label: { en: "Dental care", ar: "طب الأسنان" } },
  heroImage: "/assets/images/ai/services/dental/titanium.webp",

  en: {
    title: "Gum Treatment (Periodontics) in Istanbul",
    eyebrow: "Dental care",
    lead:
      "Gum disease begins as inflammation that bleeds when you brush, and can end with teeth that loosen and " +
      "are lost. Treatment removes the bacterial deposit from the root surface and then keeps it off. The " +
      "disease is controlled, not cured.",
    cta: "Book a free assessment",
    heroAlt: "Periodontal examination at Platinya Clinic",
    stats: [
      { value: "1–4 visits", label: "Scaling and root planing" },
      { value: "6–8 weeks", label: "Before reassessment" },
      { value: "3–6 months", label: "Maintenance interval" },
    ],
    candidate: {
      heading: "Who needs treatment",
      intro:
        "Almost anyone whose gums bleed is a candidate for initial treatment. What changes the plan is how " +
        "deep the pockets are and how much bone has already been lost.",
      items: [
        "Bleeding when brushing or flossing, even without pain",
        "Persistent bad breath or a metallic taste, with calculus visible at the gum line",
        "Gums that have receded, or teeth that look longer than they used to",
        "Pockets deeper than 4 mm on probing, or teeth that have begun to move",
      ],
      note:
        "Smoking hides the signs: it narrows the blood vessels, so the gums bleed less while the disease " +
        "carries on. Diabetes and pregnancy change the response too, which is why both are asked about.",
    },
    techniques: {
      heading: "Cleaning, surgery, or grafting",
      intro:
        "Almost every case starts with the same step — removing what is on the root. The rest is decided by " +
        "what the tissue does afterwards.",
      items: [
        {
          name: "Scaling and root planing",
          text:
            "Deep cleaning under local anaesthetic, usually one quadrant at a time, removing calculus and " +
            "bacterial deposits from below the gum line. This is the treatment for most cases; surgery is " +
            "considered only if the pockets stay deep afterwards.",
        },
        {
          name: "Curettage and flap surgery",
          text:
            "Where pockets remain deep after initial therapy, the gum is lifted, the root surfaces are " +
            "cleaned under direct vision and the tissue is repositioned. It is reserved for sites that did " +
            "not respond — not used as a first step.",
        },
        {
          name: "Gum grafting for recession",
          text:
            "Where a root is exposed, sensitive, or the recession is progressing, tissue is taken from the " +
            "palate and grafted over the root. It covers the root and thickens the gum; it does not bring " +
            "back bone that has already been lost.",
        },
      ],
    },
    journey: {
      heading: "From charting to maintenance",
      steps: [
        {
          name: "Periodontal charting",
          text:
            "Probing depth around every tooth, bleeding scores, mobility and x-rays for bone level. This " +
            "record is what makes the change measurable later — without it, treatment is judged by " +
            "impression.",
        },
        {
          name: "Initial therapy",
          text:
            "Scaling and root planing over one to four appointments, a week or two apart, with local " +
            "anaesthetic where the pockets are deep. Brushing technique is corrected at the same time, " +
            "because the cleaning only buys a fresh start.",
        },
        {
          name: "Reassessment at six to eight weeks",
          text:
            "The pockets are measured again. Shallow ones often improve by a millimetre or two; deep ones " +
            "frequently do not, and those are the sites that need a decision.",
        },
        {
          name: "Surgery, if it is needed",
          text:
            "Planned only for sites that did not respond, and only after reassessment. It is a smaller " +
            "procedure than it sounds, but it is still surgery, with its own healing time.",
        },
        {
          name: "Maintenance",
          text:
            "Visits every three to six months, with probing repeated. This is the part that decides whether " +
            "the teeth are kept: the disease returns wherever the biofilm is allowed to rebuild undisturbed.",
        },
      ],
    },
    risks: {
      heading: "What can go wrong",
      intro:
        "Most of what patients call a complication is the tissue healing. The genuine risks are different, " +
        "and worth stating plainly.",
      items: [
        "Gums shrink as inflammation settles, so teeth look longer and roots may be exposed — this is healing, not damage",
        "Sensitivity to cold on exposed roots, sometimes for weeks",
        "Bleeding and soreness for a few days after deep cleaning, and tenderness when eating",
        "The disease is chronic: without maintenance it returns, and bone already lost does not grow back",
        "In advanced cases teeth may still be lost; the aim is to keep the rest, not to promise every tooth",
      ],
      note:
        "Smoking is the most important factor you can change: it blunts the response to treatment and hides " +
        "the bleeding that would have brought you in earlier. Stopping changes the prognosis more than any " +
        "mouthwash.",
    },
    faq: {
      heading: "Questions patients ask",
      items: [
        {
          q: "Will deep cleaning loosen my teeth?",
          a:
            "No. Teeth can feel loose for a few days because the calculus that was splinting them has gone " +
            "and the swelling has settled; they firm up as the tissue heals. Where bone loss is advanced, " +
            "the tooth was already mobile before treatment.",
        },
        {
          q: "Is it a cure?",
          a:
            "No — it is control. Periodontitis does not go away, but it can be held still. Maintenance every " +
            "three to six months is what keeps it still, and skipping it is the commonest reason teeth are " +
            "lost later.",
        },
        {
          q: "Does it hurt?",
          a:
            "The cleaning is done under local anaesthetic, so not during. Afterwards the gums are tender for " +
            "two or three days and the roots may be sensitive to cold for a few weeks; both settle with " +
            "ordinary pain relief and a desensitising toothpaste.",
        },
        {
          q: "How often do I need to come back?",
          a:
            "Every three months if pockets were deep, you smoke or your diabetes is not well controlled; " +
            "every six months for milder cases. The interval is set at reassessment, from the measurements " +
            "rather than from a rule.",
        },
      ],
    },
    ctaBand: {
      heading: "Send a photograph of your gums",
      text:
        "A photograph of the gum line and a recent x-ray are enough for a first opinion on whether this is " +
        "gingivitis, periodontitis, or something that needs to be seen in the chair.",
      button: "Send your photos",
    },
    disclaimer:
      "General information, not medical advice. Gum disease can only be diagnosed by probing and imaging; " +
      "suitability for treatment is judged after examination, and results vary between patients.",
  },

  ar: {
    title: "علاج اللثة في إسطنبول",
    eyebrow: "طب الأسنان",
    lead:
      "أمراض اللثة تبدأ بالتهاب يُدمي عند التنظيف، وقد تنتهي بأسنان تتحرّك وتُفقد. والعلاج يزيل الترسبات " +
      "الجرثومية عن سطح الجذر ثم يحافظ على خلوه منها. فالمرض يُضبط ولا يُشفى نهائياً.",
    cta: "احجز تقييماً مجانياً",
    heroAlt: "فحص اللثة في عيادة بلاتينيا",
    stats: [
      { value: "١–٤ زيارات", label: "تنظيف وتجريف الجذور" },
      { value: "٦–٨ أسابيع", label: "قبل إعادة التقييم" },
      { value: "٣–٦ أشهر", label: "فاصل العناية الدورية" },
    ],
    candidate: {
      heading: "من يحتاج العلاج",
      intro:
        "كل من تنزف لثته تقريباً مرشّح للعلاج الأولي. والذي يغيّر الخطة هو عمق الجيوب ومقدار العظم المفقود " +
        "سابقاً.",
      items: [
        "نزيف عند التفريش أو استخدام الخيط، ولو دون ألم",
        "رائحة فم مستمرة أو طعم معدني، مع جير ظاهر عند خط اللثة",
        "لثة انحسرت، أو أسنان تبدو أطول مما كانت",
        "جيوب أعمق من ٤ ملم عند السبر، أو أسنان بدأت تتحرّك",
      ],
      note:
        "التدخين يخفي العلامات: يضيّق الأوعية الدموية فتنزف اللثة أقل بينما يستمر المرض. والسكري والحمل " +
        "يغيّران الاستجابة أيضاً، ولهذا يُسأل عنهما.",
    },
    techniques: {
      heading: "تنظيف أم جراحة أم ترقيع",
      intro:
        "معظم الحالات تبدأ بالخطوة نفسها — إزالة ما على الجذر. والباقي يقرّره ما تفعله الأنسجة بعد ذلك.",
      items: [
        {
          name: "التنظيف وتجريف الجذور",
          text:
            "تنظيف عميق تحت تخدير موضعي، رباعية واحدة في كل مرة عادةً، لإزالة الجير والترسبات الجرثومية من " +
            "تحت خط اللثة. وهو العلاج الأساسي لمعظم الحالات؛ ولا تُطرح الجراحة إلا إذا بقيت الجيوب عميقة " +
            "بعده.",
        },
        {
          name: "الكحت وجراحة الرتق",
          text:
            "حيث تبقى الجيوب عميقة بعد العلاج الأولي، تُرفع اللثة وتُنظَّف سطوح الجذور بالرؤية المباشرة ثم " +
            "تُعاد الأنسجة إلى موضعها. ويُحفظ هذا للمواضع التي لم تستجب — لا يُستخدم خطوة أولى.",
        },
        {
          name: "ترقيع اللثة للانحسار",
          text:
            "حيث يكون الجذر مكشوفاً أو حسّاساً أو الانحسار في تقدّم، تُؤخذ أنسجة من سقف الحلق وتُرقَّع فوق " +
            "الجذر. فذلك يغطي الجذر ويثخّن اللثة؛ لكنه لا يعيد العظم المفقود.",
        },
      ],
    },
    journey: {
      heading: "من القياس إلى العناية الدورية",
      steps: [
        {
          name: "تخطيط اللثة",
          text:
            "قياس عمق الجيب حول كل سن، وتقييم النزيف والحركة، وأشعة لتحديد مستوى العظم. هذا السجل هو ما " +
            "يجعل التغيّر قابلاً للقياس لاحقاً — وبدونه يُحكم على العلاج بالانطباع.",
        },
        {
          name: "العلاج الأولي",
          text:
            "تنظيف وتجريف الجذور على زيارة إلى أربع زيارات، بينها أسبوع أو أسبوعان، مع تخدير موضعي حيث " +
            "تكون الجيوب عميقة. وتُصحَّح طريقة التفريش في الوقت نفسه، لأن التنظيف يشتري بداية جديدة فقط.",
        },
        {
          name: "إعادة التقييم بعد ستة إلى ثمانية أسابيع",
          text:
            "تُقاس الجيوب مرة أخرى. والجيوب الضحلة تتحسّن غالباً بمليمتر أو اثنين، والعميقة كثيراً ما لا " +
            "تتحسّن، وهذه هي المواضع التي تحتاج قراراً.",
        },
        {
          name: "الجراحة إن لزمت",
          text:
            "تُخطَّط للمواضع التي لم تستجب فقط، وبعد إعادة التقييم فقط. وهي أصغر مما يبدو من اسمها، لكنها " +
            "تبقى جراحة لها زمن التئامها.",
        },
        {
          name: "العناية الدورية",
          text:
            "زيارات كل ثلاثة إلى ستة أشهر مع إعادة السبر. وهذه هي المرحلة التي تحسم بقاء الأسنان: فالمرض " +
            "يعود حيث يُترك الغشاء الحيوي الجرثومي ينمو دون إزعاج.",
        },
      ],
    },
    risks: {
      heading: "ما قد يسوء",
      intro:
        "معظم ما يسمّيه المرضى مضاعفات هو في الحقيقة التئام الأنسجة. أما المخاطر الحقيقية فمختلفة، وتجب " +
        "قولها بوضوح.",
      items: [
        "تنكمش اللثة مع هدوء الالتهاب، فتبدو الأسنان أطول وقد تنكشف الجذور — وهذا شفاء لا ضرر",
        "حساسية للبارد في الجذور المكشوفة، وأحياناً لأسابيع",
        "نزيف وألم أياماً قليلة بعد التنظيف العميق، وحساسية عند الأكل",
        "المرض مزمن: يعود دون عناية دورية، والعظم المفقود لا ينمو من جديد",
        "في الحالات المتقدّمة قد تُفقد أسنان رغم العلاج؛ والهدف حفظ الباقي لا الوعد بكل سن",
      ],
      note:
        "التدخين هو أهم عامل يمكنك تغييره: يُضعف الاستجابة للعلاج ويخفي النزيف الذي كان سيدفعك للفحص " +
        "مبكراً. والتوقّف عن التدخين يغيّر المآل أكثر من أي غسول فم.",
    },
    faq: {
      heading: "أسئلة يطرحها المرضى",
      items: [
        {
          q: "هل يُرخي التنظيف العميق الأسنان؟",
          a:
            "لا. قد تشعر الأسنان بحركة أياماً قليلة لأن الجير الذي كان يسندها أُزيل ولأن التورّم هدأ؛ ثم " +
            "تشتدّ مع التئام الأنسجة. وحيث يكون فقد العظم متقدّماً، كان السن متحرّكاً قبل العلاج أصلاً.",
        },
        {
          q: "هل هو علاج شافٍ؟",
          a:
            "لا — إنه ضبط للمرض. التهاب اللثة حول الأسنان لا يزول، لكن يمكن تثبيته. والعناية كل ثلاثة إلى " +
            "ستة أشهر هي ما يثبّته، وإهمالها هو السبب الأكثر شيوعاً لفقد الأسنان لاحقاً.",
        },
        {
          q: "هل هو مؤلم؟",
          a:
            "التنظيف يُجرى تحت تخدير موضعي فلا ألم أثناءه. بعده تكون اللثة مؤلمة يومين أو ثلاثة، وقد تبقى " +
            "الجذور حسّاسة للبارد أسابيع قليلة؛ وكلاهما يهدأ بمسكّن عادي ومعجون مزيل للتحسّس.",
        },
        {
          q: "كم مرة يجب أن أعود؟",
          a:
            "كل ثلاثة أشهر إذا كانت الجيوب عميقة أو كنت مدخّناً أو سكريّك غير مضبوط؛ وكل ستة أشهر في " +
            "الحالات الأخفّ. ويُحدَّد الفاصل في إعادة التقييم من القياسات لا من قاعدة ثابتة.",
        },
      ],
    },
    ctaBand: {
      heading: "أرسل صورة للثتك",
      text:
        "صورة لخط اللثة وأشعة حديثة تكفيان لرأي أوّلي: هل الأمر التهاب لثة بسيط أم التهاب حول الأسنان أم " +
        "حالة يجب فحصها في العيادة.",
      button: "أرسل صورك",
    },
    disclaimer:
      "معلومات عامة وليست نصيحة طبية. لا يُشخَّص مرض اللثة إلا بالسبر والتصوير؛ وتُقيَّم ملاءمة العلاج بعد " +
      "الفحص، والنتائج تختلف من مريض لآخر.",
  },
};
